import styled from '@emotion/styled';
import { COLORS } from 'styles/constants';
import { useEffect, useRef, useState } from 'react';
import { EventProps, Event as RBCEvent } from 'react-big-calendar';
import dayjs from 'lib/dayjs';
import 'moment/locale/ko';
import { truncate } from 'styles/utils';
import { has } from 'lodash';
import { Checkbox, Popover, TextField } from '@mui/material';
import { TIME_FORMAT_2 } from 'utils/datetimeFormat';
import PriorityIssueCheckbox from 'components/PriorityIssueCheckbox';
import { Icons } from 'components';
import { OutCategory, OutTaskboxDetailResponseCategory } from 'queries/model';
import { getCategoryBgColor, getCategoryTextColor } from 'utils/category';
import { GAEventTrigger } from 'lib/gtag';
import CategoryPopover, { CategoryActionType } from '../components/CategoryPopover';
import { useAtom } from 'jotai';
import { categoryAtom } from 'atoms/category';
import { setCaretToEnd } from 'utils';

const Container = styled.div<{ done?: boolean; allDay?: boolean; calendarColor?: string }>`
  height: 100%;
  width: 100%;
  background-color: ${(props) => (props.calendarColor ? `${props.calendarColor}` : !props.allDay ? 'transparent' : props.done ? '#F9FAFE' : 'white')};
  border-radius: 8px;
`;

const EventWrapper = styled.div<{ done?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 8px;
  height: 28px;
`;

const AllDayEventWrapper = styled.div<{ done?: boolean }>`
  display: flex;
  align-items: center;
  height: 100%;
  width: 100%;
  padding: 6px 8px;
  height: 28px;
`;

const TextTruncate = styled.div`
  ${truncate('auto')};
`;

const AllDayEventTitle = styled.div<{ done?: boolean; color?: string }>`
  width: 100%;
  flex: 1;
  font-size: 11px;
  font-weight: bold;
  color: ${(props) => props.color};
  ${truncate('auto')};
  display: flex;
  align-items: center;
`;

const EventTitle = styled.div<{ done?: boolean; color?: string }>`
  width: 100%;
  flex: 1;
  font-size: 11px;
  font-weight: bold;
  color: ${(props) => props.color};
  ${truncate('auto')};
  display: flex;
  align-items: center;
`;

const VerticalBar = styled.div<{ done?: boolean; isRecurrence?: boolean; isProject?: boolean }>`
  border-right: ${(props) => `2px solid ${props.isProject ? COLORS.issue2 : props.isRecurrence ? COLORS.sub4 : COLORS.brand1}`};
  outline: 1px solid #e7eaf4;
  border-radius: 12px;
  opacity: ${(props) => (props.done ? 0.5 : 1)};
`;

const VerticalBarFocus = styled.div<{ done?: boolean }>`
  border: 1px solid transparent;
  outline: 1px solid #e7eaf4;
  border-radius: 12px;
  -webkit-border-image: linear-gradient(180deg, #c471ed 1.78%, #f64f59 97.94%);
  border-image: linear-gradient(180deg, #c471ed 1.78%, #f64f59 97.94%);
  border-image-slice: 1;
  opacity: ${(props) => (props.done ? 0.5 : 1)};
`;

const CirclePoint = styled.div<{ done?: boolean; meeting?: boolean; isRecurrence?: boolean; isProject?: boolean }>`
  width: 6px;
  height: 6px;
  background-color: ${(props) => (props.isProject ? COLORS.issue2 : props.meeting ? COLORS.gray400 : props.isRecurrence ? COLORS.sub4 : COLORS.brand1)};
  border-radius: 50%;
  opacity: ${(props) => (props.done ? 0.5 : 1)};
`;

const CirclePointFocus = styled.div<{ done?: boolean }>`
  width: 6px;
  height: 6px;
  border: 1px solid transparent;
  outline: 1px solid #e7eaf4;
  border-radius: 12px;
  -webkit-border-image: linear-gradient(180deg, #c471ed 1.78%, #f64f59 97.94%);
  border-image: linear-gradient(180deg, #c471ed 1.78%, #f64f59 97.94%);
  border-image-slice: 1;
  border-image-width: 6px;
  border-image-repeat: no-repeat;
  opacity: ${(props) => (props.done ? 0.5 : 1)};
`;

const EditableTextField = styled.div`
  width: 100%;
  /* height: 100%; */
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 11px;
  font-weight: bold;
  color: ${COLORS.gray900};
  flex: 1;
`;

const CategoryShowingWrapper = styled.div<{ textColor?: string; bgColor?: string }>`
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.bgColor};
  border-radius: 4px;
  color: ${(props) => props.textColor};
  font-size: 10px;
  padding: 2px;
`;

export interface TaskboxInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onClickCategory?: (category: OutCategory, action: CategoryActionType) => void;
}

const TaskboxInput = ({ value, onChange, onClickCategory }: TaskboxInputProps) => {
  const refInput = useRef<HTMLInputElement>(null);
  const [categoryAnchorEl, setCategoryAnchorEl] = useState<null | HTMLElement>(null);
  const [categoryList] = useAtom(categoryAtom);

  useEffect(() => {
    refInput?.current?.focus();
  }, []);

  const handleKeydownInput = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!refInput.current) return;

    const content = refInput.current?.textContent || '';
    if (e.key === 'Escape') {
      return;
    }

    if (e.key === 'Enter') {
      if (e.nativeEvent.isComposing) return;
      if (e.repeat) {
        e.preventDefault();
        return;
      }
      if (categoryAnchorEl) {
        e.preventDefault();
        return;
      }
      onChange?.(content);
    }

    if (e.key === '#') {
      setTimeout(() => {
        setCategoryAnchorEl(refInput.current);
      }, 100);
    }
  };

  const handleBlurTextField = (e: React.FocusEvent<HTMLInputElement>) => {
    if (categoryAnchorEl) return;
    const content = e.currentTarget.textContent || '';
    setTimeout(() => onChange?.(content), 100);
  };

  const handleClickCategory = (category: OutCategory, action: CategoryActionType) => {
    onClickCategory?.(category, action);

    if (refInput.current?.textContent?.includes('#')) {
      refInput.current.textContent = refInput.current.textContent.replace(/#/g, '');
    }

    setTimeout(() => {
      setCategoryAnchorEl(null);
      refInput.current?.focus();
      setCaretToEnd(refInput.current as HTMLElement);
    }, 100);
  };

  return (
    <>
      <EditableTextField
        ref={refInput}
        contentEditable={true}
        suppressContentEditableWarning={true}
        onKeyDown={handleKeydownInput}
        onBlur={handleBlurTextField}
        dangerouslySetInnerHTML={{ __html: value || '' }}
      />
      {/* 태스크박스 카테고리 */}
      {categoryAnchorEl && (
        <Popover
          open={Boolean(categoryAnchorEl)}
          anchorEl={categoryAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          onClose={() => {
            setCategoryAnchorEl(null);
            setCaretToEnd(refInput.current as HTMLElement);
          }}
          sx={{ marginTop: '4px' }}
        >
          <CategoryPopover categoryList={categoryList} onClickCategoryAction={handleClickCategory} />
        </Popover>
      )}
    </>
  );
};

const TaskboxAllDayInput = ({ value, onChange, onClickCategory }: TaskboxInputProps) => {
  const refInput = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState(value);
  const [categoryAnchorEl, setCategoryAnchorEl] = useState<null | HTMLElement>(null);
  const [categoryList] = useAtom(categoryAtom);

  useEffect(() => {
    refInput?.current?.focus();
  }, []);

  const handleKeydownInput = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!refInput.current) return;

    if (e.key === 'Escape') {
      return;
    }

    if (e.key === 'Enter') {
      if (e.nativeEvent.isComposing) return;
      if (e.repeat) {
        e.preventDefault();
        return;
      }
      if (categoryAnchorEl) {
        e.preventDefault();
        return;
      }
      onChange?.(`${inputValue}`.trim());
    }

    if (e.key === '#') {
      setTimeout(() => {
        setCategoryAnchorEl(refInput.current as HTMLElement);
      }, 100);
    }
  };

  const handleBlurTextField = (e: React.FocusEvent<HTMLInputElement>) => {
    if (categoryAnchorEl) return;
    const content = e.currentTarget.value || '';
    setTimeout(() => onChange?.(content), 100);
  };

  const handleClickCategory = (category: OutCategory, action: CategoryActionType) => {
    onClickCategory?.(category, action);
    if (inputValue && inputValue.includes('#')) setInputValue(inputValue.replace(/#/g, ''));

    setTimeout(() => {
      setCategoryAnchorEl(null);
      refInput.current?.focus();
      setCaretToEnd(refInput.current as HTMLElement);
    }, 100);
  };

  return (
    <>
      <TextField
        ref={refInput}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        autoFocus={true}
        autoComplete="off"
        fullWidth
        variant="standard"
        onBlur={handleBlurTextField}
        onKeyDown={handleKeydownInput}
        InputProps={{ disableUnderline: true, style: { fontSize: 11, fontWeight: 'bold', color: COLORS.gray900 } }}
      />
      {/* 태스크박스 카테고리 */}
      {categoryAnchorEl && (
        <Popover
          open={Boolean(categoryAnchorEl)}
          anchorEl={categoryAnchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          onClose={() => {
            setCategoryAnchorEl(null);
            setCaretToEnd(refInput.current as HTMLElement);
          }}
          sx={{ marginTop: '4px' }}
        >
          <CategoryPopover categoryList={categoryList} onClickCategoryAction={handleClickCategory} />
        </Popover>
      )}
    </>
  );
};

export interface MonthCalendarEventProps extends EventProps<RBCEvent> {
  event: RBCEvent & {
    id?: string;
    type?: string;
    done?: boolean;
    isRecurrence?: boolean;
    isProject?: boolean;
    lockedIn?: boolean;
    focus?: boolean;
    category?: OutTaskboxDetailResponseCategory;
    calendarColor?: string;
    data?: {
      id: string;
      content: string;
      done: boolean;
      simple: boolean;
    }[];
  };
  onInput?: (event: RBCEvent, title: string) => void;
  onChangeCategory?: (category: OutTaskboxDetailResponseCategory, action: CategoryActionType) => void;
  onContextMenu?: (event: RBCEvent) => void;
}
const MonthCalendarEvent = (props: MonthCalendarEventProps) => {
  const { event, onInput, onContextMenu, onChangeCategory } = props;
  const [editing, setEditing] = useState(false);
  const range = `${dayjs(event.start).format('a h:mm')} ~ ${dayjs(event.end).format('a h:mm')}`;
  const isGreaterThanMinimum = Math.abs(dayjs(event?.start).diff(event?.end, 'minutes')) > 15;
  const textColor = event.done ? COLORS.gray400 : COLORS.gray900;
  const isNewEvent = event.type === 'task' && !has(event, 'data');

  useEffect(() => {
    if (event.type === 'task' && !has(event, 'data')) {
      setTimeout(() => setEditing(true), 100);
    }
  }, [event]);

  const handleCreate = (value: string) => {
    setEditing(false);
    onInput?.(event, value);
  };

  const handleClickCategory = (category: OutCategory, action: CategoryActionType) => {
    onChangeCategory?.(category, action);
  };

  const handleContextMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    onContextMenu?.(event);
  };

  return (
    <Container
      done={event.type === 'task' && event?.done}
      allDay={event.allDay}
      calendarColor={event.calendarColor}
      data-id={event.id}
      onContextMenu={(e) => {
        handleContextMenu(e);
        GAEventTrigger({ action: 'timebox_menu_right_click', category: 'timebox_menu_right_click' });
      }}
      style={{ borderRadius: event.type === 'meeting' ? '7px' : '8px' }}
    >
      {event.allDay ? (
        <AllDayEventWrapper>
          {event?.type === 'task' &&
            (event?.focus ? (
              <VerticalBarFocus done={event.done} style={{ marginRight: 6, height: '100%' }} />
            ) : (
              <VerticalBar done={event.done} isRecurrence={event.isRecurrence} isProject={event.isProject} style={{ marginRight: 6, height: '100%' }} />
            ))}
          {editing ? (
            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              {event?.type === 'task' &&
                (event.data && event.data!.filter((task: { done: any }) => task.done).length > 0 && !event.done ? (
                  <Checkbox
                    checked={false}
                    icon={
                      event.focus ? (
                        <Icons.FocusInprogressCheck />
                      ) : (
                        <Icons.InprogressCheck fill={event.isProject ? COLORS.issue2 : event.isRecurrence ? COLORS.sub4 : COLORS.brand1} />
                      )
                    }
                    sx={{ padding: 0, margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    style={{ width: 12, height: 12, marginRight: 4, opacity: event.done ? 0.5 : 1 }}
                  />
                ) : (
                  <PriorityIssueCheckbox
                    width={12}
                    height={12}
                    focus={event.focus}
                    bordercolor={event.isProject ? COLORS.issue2 : event.isRecurrence ? COLORS.sub4 : COLORS.brand1}
                    checked={!!event.done}
                    style={{ marginRight: 4, opacity: event.done ? 0.5 : 1 }}
                  />
                ))}
              {event?.lockedIn && <span style={{ marginRight: 4, fontSize: '11px' }}>{event.done ? '✅' : '🌟'}</span>}
              <TaskboxAllDayInput value={event.title as string} onChange={handleCreate} onClickCategory={handleClickCategory} />
            </div>
          ) : (
            <AllDayEventTitle done={event.done} color={textColor}>
              {event?.type === 'task' &&
                (event.data && event.data!.filter((task: { done: any }) => task.done).length > 0 && !event.done ? (
                  <Checkbox
                    checked={false}
                    icon={
                      event.focus ? (
                        <Icons.FocusInprogressCheck />
                      ) : (
                        <Icons.InprogressCheck fill={event.isProject ? COLORS.issue2 : event.isRecurrence ? COLORS.sub4 : COLORS.brand1} />
                      )
                    }
                    sx={{ padding: 0, margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    style={{ width: 12, height: 12, marginRight: 4, opacity: event.done ? 0.5 : 1 }}
                  />
                ) : (
                  <PriorityIssueCheckbox
                    width={12}
                    height={12}
                    focus={event.focus}
                    bordercolor={event.isProject ? COLORS.issue2 : event.isRecurrence ? COLORS.sub4 : COLORS.brand1}
                    checked={!!event.done}
                    style={{ marginRight: 4, opacity: event.done ? 0.5 : 1 }}
                  />
                ))}
              {event?.lockedIn && <span style={{ marginRight: 4, fontSize: '11px' }}>{event.done ? '✅' : '🌟'}</span>}
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  marginTop: '2px',
                  textDecoration: event.done ? 'line-through' : '',
                }}
              >
                {isNewEvent ? '' : event.title || '제목 없음'}
              </span>
            </AllDayEventTitle>
          )}
          {event.category && (
            <CategoryShowingWrapper
              textColor={getCategoryTextColor(event.category.color)}
              bgColor={getCategoryBgColor(event.category.color)}
              style={{
                border: event.type === 'meeting' ? `1px solid ${getCategoryTextColor(event.category.color)}` : 'none',
              }}
            >
              #
            </CategoryShowingWrapper>
          )}
        </AllDayEventWrapper>
      ) : (
        <EventWrapper>
          {event?.focus ? (
            <CirclePointFocus done={event.done} style={{ marginRight: 6 }} />
          ) : (
            <CirclePoint
              done={event.done}
              meeting={event.type === 'meeting'}
              isRecurrence={event.isRecurrence}
              isProject={event.isProject}
              style={{ marginRight: 6 }}
            />
          )}
          <div style={{ display: 'flex', flexDirection: 'column', width: '90%', overflow: 'hidden' }}>
            {editing ? (
              <>
                {event?.lockedIn && <span style={{ marginRight: 4, fontSize: '11px' }}>{event.done ? '✅' : '🌟'}</span>}
                <TaskboxInput value={event.title as string} onChange={handleCreate} onClickCategory={handleClickCategory} />
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <EventTitle done={event.done} color={textColor}>
                  {event?.type === 'task' &&
                    (event.data && event.data!.filter((task: { done: any }) => task.done).length > 0 && !event.done ? (
                      <Checkbox
                        checked={false}
                        icon={
                          event.focus ? (
                            <Icons.FocusInprogressCheck />
                          ) : (
                            <Icons.InprogressCheck fill={event.isProject ? COLORS.issue2 : event.isRecurrence ? COLORS.sub4 : COLORS.brand1} />
                          )
                        }
                        sx={{ padding: 0, margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        style={{ width: 12, height: 12, marginRight: 4, opacity: event.done ? 0.5 : 1 }}
                      />
                    ) : (
                      <PriorityIssueCheckbox
                        width={12}
                        height={12}
                        focus={event.focus}
                        bordercolor={event.isProject ? COLORS.issue2 : event.isRecurrence ? COLORS.sub4 : COLORS.brand1}
                        checked={!!event.done}
                        style={{ marginRight: 4, opacity: event.done ? 0.5 : 1 }}
                      />
                    ))}
                  {event?.lockedIn && <span style={{ marginRight: 4, fontSize: '11px' }}>{event.done ? '✅' : '🌟'}</span>}
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      marginTop: '2px',
                      textDecoration: event.done ? 'line-through' : '',
                    }}
                  >
                    {isNewEvent ? '' : event.title || '제목 없음'}
                  </span>
                </EventTitle>
                {event.category ? (
                  <CategoryShowingWrapper
                    textColor={getCategoryTextColor(event.category.color)}
                    bgColor={getCategoryBgColor(event.category.color)}
                    style={{
                      border: event.type === 'meeting' ? `1px solid ${getCategoryTextColor(event.category.color)}` : 'none',
                    }}
                  >
                    #
                  </CategoryShowingWrapper>
                ) : (
                  <span style={{ fontSize: 8, fontWeight: 400, color: COLORS.gray500 }}>{dayjs(event.start).format(TIME_FORMAT_2)}</span>
                )}
              </div>
            )}
          </div>
        </EventWrapper>
      )}
    </Container>
  );
};

export default MonthCalendarEvent;
